import { AzureTestServerModule } from './azure/azure.module';
import { SharedModule } from './shared/shared.module';
import { PulumiModule } from './pulumi/pulumi.module';
import { NgModule, inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { BrowserAnimationsModule, NoopAnimationsModule } from '@angular/platform-browser/animations';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ReactiveFormsModule } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { ToastrModule } from 'ngx-toastr';
import { MsalGuard, MsalInterceptor, MsalModule, MsalService } from '@azure/msal-angular';
import { AuthRedirectComponent } from './shared/components/auth-redirect/auth-redirect.component';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { NgSelectModule } from '@ng-select/ng-select';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatList, MatListModule } from '@angular/material/list';
import { SidebarComponent } from './sidebar/sidebar.component';
import { WhitelabelModule } from './whitelabel/whitelabel.module';
import { NiboWhatsappModule } from './nibo-whatsapp/nibo-whatsapp.module';

@NgModule({
  declarations: [
    AppComponent,
    SidebarComponent,
    AuthRedirectComponent,
  ],
  imports: [
    NiboWhatsappModule,
    WhitelabelModule,
    MatListModule,
    MatTooltipModule,
    MatIconModule,
    NgSelectModule,
    MatSelectModule,
    MatDialogModule,
    BrowserModule,
    AppRoutingModule,
    PulumiModule,
    HttpClientModule,
    NgbModule,
    NoopAnimationsModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    SharedModule,
    BrowserAnimationsModule,
    FormsModule,
    AzureTestServerModule,
    ToastrModule.forRoot({
      timeOut: 3000,
      positionClass: 'toast-bottom-right'
    }),
    MsalModule.forRoot(new PublicClientApplication(
      {
        auth: {
          clientId: '7264e5db-b8a1-4fe9-bb4d-5aa6eff8244f',
          authority: 'https://login.microsoftonline.com/4ae707c8-e0df-430e-b70e-3ec3551471a6',
          // Fixados na raiz em vez do default (que e a URL atual, sem query/hash): o
          // botao de login existe em toda pagina e a AAD exige match exato do redirect
          // URI contra os registrados no app registration. Como navigateToLoginRequestUrl
          // vem true por padrao, o MSAL devolve o usuario para a rota de origem depois
          // de processar a resposta.
          redirectUri: '/',
          postLogoutRedirectUri: '/'
        },
        cache: {
          // storeAuthStateInCookie foi removido do CacheOptions no msal-browser v5.
          cacheLocation: 'localStorage'
        }
      }
    ),
      {
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: ['user.read']
        }
      },
      {
        interactionType: InteractionType.Redirect,
        // O sufixo `/*` e obrigatorio a partir do msal-angular v5: o interceptor passou a
        // casar as URLs com strictMatching (padrao true), que ancora o padrao de path.
        // Uma chave sem path vira pathname "/" e o regex ^\/$ nao casa com "/v1.0/me" --
        // resultado: nenhum scope encontrado e a request sai sem Authorization.
        protectedResourceMap: new Map(
          [
            ['https://whatsapp-api.nibo.com.br/*', ['https://graph.microsoft.com/.default']],
            ['https://test-whatsapp-api.nibo.com.br/*', ['https://graph.microsoft.com/.default']],
            ['http://localhost:5192/*', ['https://graph.microsoft.com/.default']],
            ['https://graph.microsoft.com/*', ['https://graph.microsoft.com/.default']],
            ['https://dev.azure.com/*', ['499b84ac-1321-427f-aa17-267ca6975798/.default']],
            ['https://management.azure.com/*', ['https://management.azure.com/.default']],
            ['https://storage.azure.com/*', ['https://storage.azure.com/.default']],
            ['https://brniboappwhitelabel.blob.core.windows.net/*', ['https://storage.azure.com/.default']],
            ['https://br-nibo-customer-app-whitelabel-functions.azurewebsites.net/*', ['api://f27973b9-fa23-4625-8045-9f42b40013b5/user_impersonation']],
            ['https://br-nibo-customer-app-whitelabel-functions-test.azurewebsites.net/*', ['api://f27973b9-fa23-4625-8045-9f42b40013b5/user_impersonation']],
            // O environment de desenvolvimento aponta a api de whitelabel para o Functions rodando
            // local (porta do local.settings.json). Sem esta entrada a request sai sem Authorization
            // e o EntraIdAuthenticationMiddleware do Function App responde 401 -- o token e o mesmo
            // dos hosts publicados, entao basta o dev estar logado.
            ['http://localhost:7089/*', ['api://f27973b9-fa23-4625-8045-9f42b40013b5/user_impersonation']]
          ]
        )
      })
  ],
  providers: [
    // Change detection dirigida por Signals, sem zone.js. Todo estado que a tela le e
    // que muda em callback assincrono (respostas HTTP, eventos de SignalR) esta em
    // signal, e e a escrita no signal que agenda a renderizacao. Eventos de template
    // continuam agendando CD normalmente, entao estado que so muda por interacao do
    // usuario segue como campo comum.
    //
    // Zoneless ja e o padrao no Angular 21; declarar explicitamente documenta a escolha
    // e evita depender de um default que pode mudar.
    provideZonelessChangeDetection(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    MsalGuard,
    // O msal-browser v3+ exige initialize() antes de qualquer leitura do cache, e no v5
    // esse initialize e assincrono porque deriva a chave de encriptacao do localStorage.
    // Sem bloquear o bootstrap, qualquer getActiveAccount()/getAllAccounts() que rode no
    // primeiro change detection estoura BrowserAuthError: uninitialized_public_client_application
    // (foi o caso do getter canTakeScreenshot em ConnectionsListComponent, avaliado a cada
    // ciclo de CD). Aguardar aqui garante que o cache esteja pronto antes de qualquer
    // componente renderizar, sem precisar proteger cada call site individualmente.
    provideAppInitializer(() => inject(MsalService).initialize())
  ],
  bootstrap: [AppComponent, AuthRedirectComponent]
})
export class AppModule { }
